import { useId } from 'react';

import styles from './AltTextField.module.css';

interface AltTextFieldProps {
  disabled?: boolean;
  onChange: (altText: string) => void;
  value?: string;
}

// The alt text control, shared by the image content unit editor and the question
// prompt-image dialog so the two cannot drift.
//
// A textarea rather than an input: a useful description is a sentence or two,
// and a single-line box that scrolls sideways quietly encourages three words.
export function AltTextField({
  disabled = false,
  onChange,
  value,
}: AltTextFieldProps) {
  const fieldId = useId();
  const hintId = useId();
  const isMissing = !value?.trim();

  return (
    <div className={styles.root}>
      <label className={styles.label} htmlFor={fieldId}>
        Alt text
      </label>
      {/* Most instructors have never written alt text. The one-line version of
          what it is for does more than a label alone. */}
      <p className={styles.help} id={hintId}>
        Describe what the image shows, for students who use a screen reader or
        whose image does not load. Leave it empty if the image is decorative.
      </p>
      <textarea
        aria-describedby={hintId}
        className={styles.input}
        disabled={disabled}
        id={fieldId}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        placeholder="Enter an alternate text description for students with screen readers"
        rows={3}
        value={value ?? ''}
      />
      {isMissing ? (
        // Informational, never blocking. An instructor often cannot write a good
        // description at the moment they upload, and a blocked save would only
        // teach them to type a placeholder.
        <p className={styles.missing} role="note">
          No description yet — students using a screen reader will not hear what
          this image shows.
        </p>
      ) : null}
    </div>
  );
}
